/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-09-12 19:42:43
 */

import { useEffect, useMemo, useRef } from 'react'
import { useAppSelector, useUserId } from './hooks'

type AuthPolicy = {
  departmentIDList?: (number | string)[]
  userIDList?: (string | undefined)[]
} | null

/**
 * 构建部门查看权限的hook
 * @returns AuthPolicy
 */
export const useDepartmentAuth: () => AuthPolicy = () => {
  const group = useAppSelector((state) => state.group)
  const userID = useUserId()
  const groupAuthPolicy = group.groupAuthPolicy
  const authPolicy = useMemo(() => {
    if (groupAuthPolicy) {
      //检查角色policy: policyType === 2, source === 2, action === 'read'
      const userPolicy = groupAuthPolicy.policyList.find((policy) => {
        return (
          policy.policyType === 2 &&
          policy.source === 2 &&
          policy.action === 'read'
        )
      })
      if (groupAuthPolicy.groupDepartmentView === 0) {
        // 查看全部
        return {
          departmentIDList: [0]
        }
      } else if (groupAuthPolicy.groupDepartmentView === 1) {
        //只看自己
        if (userPolicy) {
          //判断defaultAuthority: 1所在团队，2所在部门，3自己
          if (userPolicy.defaultAuthority === 1) {
            return {
              departmentIDList: [0]
            }
          } else if (userPolicy.defaultAuthority === 2) {
            return {
              departmentIDList: [
                ...groupAuthPolicy.departmentIDs,
                ...userPolicy.extendDepartment
              ],
              userIDList: userPolicy.extendUser
            }
          } else {
            return {
              departmentIDList: userPolicy.extendDepartment,
              userIDList: [userID, ...userPolicy.extendUser]
            }
          }
        }
        return {
          userIDList: [userID]
        }
      } else {
        //只看所在部门
        if (userPolicy) {
          //判断defaultAuthority: 1所在团队，2所在部门，3自己
          if (userPolicy.defaultAuthority === 1) {
            return {
              departmentIDList: [0]
            }
          } else {
            // 2和3是一样的逻辑
            return {
              departmentIDList: [
                ...groupAuthPolicy.departmentIDs,
                ...userPolicy.extendDepartment
              ],
              userIDList: userPolicy.extendUser
            }
          }
        } else {
          return {
            departmentIDList: groupAuthPolicy.departmentIDs
          }
        }
      }
    } else {
      return null
    }
  }, [groupAuthPolicy, userID])
  return authPolicy
}

/**
 * 构建部门查看权限的方法，用于在非函数组件中使用
 * @returns AuthPolicy
 */

export const createDepartmentAuth: (
  group: any,
  userID: string
) => AuthPolicy = (group, userID) => {
  const groupAuthPolicy = group.groupAuthPolicy
  if (groupAuthPolicy) {
    //检查角色policy: policyType === 2, source === 2, action === 'read'
    const userPolicy = groupAuthPolicy.policyList.find((policy) => {
      return (
        policy.policyType === 2 &&
        policy.source === 2 &&
        policy.action === 'read'
      )
    })
    if (groupAuthPolicy.groupDepartmentView === 0) {
      // 查看全部
      return {
        departmentIDList: [0]
      }
    } else if (groupAuthPolicy.groupDepartmentView === 1) {
      //只看自己
      if (userPolicy) {
        //判断defaultAuthority: 1所在团队，2所在部门，3自己
        if (userPolicy.defaultAuthority === 1) {
          return {
            departmentIDList: [0]
          }
        } else if (userPolicy.defaultAuthority === 2) {
          return {
            departmentIDList: [
              ...groupAuthPolicy.departmentIDs,
              ...userPolicy.extendDepartment
            ],
            userIDList: userPolicy.extendUser
          }
        } else {
          return {
            departmentIDList: userPolicy.extendDepartment,
            userIDList: [userID, ...userPolicy.extendUser]
          }
        }
      }
      return {
        userIDList: [userID]
      }
    } else {
      //只看所在部门
      if (userPolicy) {
        //判断defaultAuthority: 1所在团队，2所在部门，3自己
        if (userPolicy.defaultAuthority === 1) {
          return {
            departmentIDList: [0]
          }
        } else {
          // 2和3是一样的逻辑
          return {
            departmentIDList: [
              ...groupAuthPolicy.departmentIDs,
              ...userPolicy.extendDepartment
            ],
            userIDList: userPolicy.extendUser
          }
        }
      } else {
        return {
          departmentIDList: groupAuthPolicy.departmentIDs
        }
      }
    }
  }
  return null
}

/**
 * 构建照片查看权限的方法，用于在非函数组件中使用
 * @returns AuthPolicy
 */

export const createQueryPhotoAuth: (
  group: any,
  userID: string
) => AuthPolicy = (group, userID) => {
  const groupAuthPolicy = group.groupAuthPolicy
  if (groupAuthPolicy) {
    //检查角色policy: policyType === 2, source === 1, action === 'read'
    const userPolicy = groupAuthPolicy.policyList.find((policy) => {
      return (
        policy.policyType === 2 &&
        policy.source === 1 &&
        policy.action === 'read'
      )
    })
    if (groupAuthPolicy.groupViewPerm === 0) {
      // 查看全部
      return {
        departmentIDList: [0]
      }
    } else if (groupAuthPolicy.groupViewPerm === 1) {
      //只看自己
      if (userPolicy) {
        //判断defaultAuthority: 1所在团队，2所在部门，3自己
        if (userPolicy.defaultAuthority === 1) {
          return {
            departmentIDList: [0]
          }
        } else if (userPolicy.defaultAuthority === 2) {
          return {
            departmentIDList: [
              ...groupAuthPolicy.departmentIDs,
              ...userPolicy.extendDepartment
            ],
            userIDList: userPolicy.extendUser
          }
        } else {
          return {
            departmentIDList: userPolicy.extendDepartment,
            userIDList: [userID, ...userPolicy.extendUser]
          }
        }
      }
      return {
        userIDList: [userID]
      }
    } else {
      //只看所在部门
      if (userPolicy) {
        //判断defaultAuthority: 1所在团队，2所在部门，3自己
        if (userPolicy.defaultAuthority === 1) {
          return {
            departmentIDList: [0]
          }
        } else {
          // 2和3是一样的逻辑
          return {
            departmentIDList: [
              ...groupAuthPolicy.departmentIDs,
              ...userPolicy.extendDepartment
            ],
            userIDList: userPolicy.extendUser
          }
        }
      } else {
        return {
          departmentIDList: groupAuthPolicy.departmentIDs
        }
      }
    }
  }
  return null
}

/**
 * 构建客户相关查看权限的方法，用于在非函数组件中使用
 * @returns AuthPolicy
 */

export const createQueryCustomerAuth: (
  group: any,
  userID: string
) => AuthPolicy = (group, userID) => {
  const groupAuthPolicy = group.groupAuthPolicy
  if (groupAuthPolicy) {
    //检查角色policy: policyType === 2, source === 4, action === 'read'
    const userPolicy = groupAuthPolicy.policyList.find((policy) => {
      return (
        policy.policyType === 2 &&
        policy.source === 4 &&
        policy.action === 'read'
      )
    })

    if (userPolicy) {
      //判断defaultAuthority: 1所在团队，2所在部门，3自己
      if (userPolicy.defaultAuthority === 1) {
        return {
          departmentIDList: [0]
        }
      } else if (userPolicy.defaultAuthority === 2) {
        return {
          departmentIDList: [
            ...groupAuthPolicy.departmentIDs,
            ...userPolicy.extendDepartment
          ],
          userIDList: userPolicy.extendUser
        }
      } else {
        return {
          departmentIDList: userPolicy.extendDepartment,
          userIDList: [userID, ...userPolicy.extendUser]
        }
      }
    }
    return {
      userIDList: [userID]
    }
  }
  return null
}

/**
 * 构建拜访相关查看权限的方法，用于在非函数组件中使用
 * @returns AuthPolicy
 */

export const createQueryVisiteAuth: (
  group: any,
  userID: string
) => AuthPolicy = (group, userID) => {
  const groupAuthPolicy = group.groupAuthPolicy
  if (groupAuthPolicy) {
    //检查角色policy: policyType === 2, source === 5, action === 'read'
    const userPolicy = groupAuthPolicy.policyList.find((policy) => {
      return (
        policy.policyType === 2 &&
        policy.source === 5 &&
        policy.action === 'read'
      )
    })

    if (userPolicy) {
      //判断defaultAuthority: 1所在团队，2所在部门，3自己
      if (userPolicy.defaultAuthority === 1) {
        return {
          departmentIDList: [0]
        }
      } else if (userPolicy.defaultAuthority === 2) {
        return {
          departmentIDList: [
            ...groupAuthPolicy.departmentIDs,
            ...userPolicy.extendDepartment
          ],
          userIDList: userPolicy.extendUser
        }
      } else {
        return {
          departmentIDList: userPolicy.extendDepartment,
          userIDList: [userID, ...userPolicy.extendUser]
        }
      }
    }
    return {
      userIDList: [userID]
    }
  }
  return null
}
